import { Bell, Package, Plus } from "@/components/icons";
import { Badge, Button, Skeleton } from "@/components/kit";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { RestockWaitlistListItemDTO } from "@/modules/restock-waitlist/domain/types";
import { useRestockWaitlistListQuery } from "@/modules/restock-waitlist/presentation/api/queries";
import {
  getWaitlistStatusMeta,
  waitlistStatusLabel,
} from "@/modules/restock-waitlist/presentation/ui/waitlist-status";
import { formatDateLocal } from "@/shared/lib/date-time";
import { Link } from "@tanstack/react-router";

type CustomerWaitlistSectionProps = {
  customerId: string;
  customerName: string;
};

function WaitlistRow({ row }: { row: RestockWaitlistListItemDTO }) {
  const meta = getWaitlistStatusMeta(row.status);

  return (
    <Link
      to="/app/restock-waitlist/$id"
      params={{ id: row.id }}
      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/50 bg-background/70 px-4 py-3 transition-colors hover:border-secondary/35 hover:bg-secondary/5"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-secondary tabular-nums">
            {row.waitNumber}
          </span>
          <Badge
            variant={meta.variant}
            className={cn("rounded-full text-xs", meta.className)}
          >
            {waitlistStatusLabel(row.status)}
          </Badge>
        </div>
            <p className="text-muted-foreground text-sm">{row.summaryLabel}</p>
        <p className="mt-0.5 text-muted-foreground text-xs">
          {formatDateLocal(row.createdAt)}
          {row.lines.length === 1 && row.lines[0]?.queuePosition != null
            ? ` · ຄິວ #${row.lines[0].queuePosition}`
            : row.lineCount > 1
              ? ` · ${row.lineCount} ລາຍການ`
              : ""}
        </p>
      </div>
      <div className="text-right text-muted-foreground text-xs">
        <p>
          {row.lines.length === 1
            ? `ສະຕັອກ ${row.lines[0]?.stockOnHand ?? 0}`
            : `${row.lineCount} ລາຍການ`}
        </p>
      </div>
    </Link>
  );
}

export function CustomerWaitlistSection({
  customerId,
  customerName,
}: CustomerWaitlistSectionProps) {
  const canReadWaitlist = useActionPermission(["restock_waitlist:read"]);
  const canWriteWaitlist = useActionPermission(["restock_waitlist:write"]);

  const waitlist = useRestockWaitlistListQuery(
    { customerId, limit: 10, offset: 0 },
    canReadWaitlist,
  );

  if (!canReadWaitlist) return null;

  const rows = waitlist.data?.data ?? [];
  const total = waitlist.data?.meta.total ?? 0;
  const activeCount = rows.filter((row) =>
    ["waiting", "ready", "notified"].includes(row.status),
  ).length;

  return (
    <section className="brand-card flex flex-col gap-4 overflow-hidden p-0">
      <div className="flex flex-wrap items-start justify-between gap-3 border-secondary/15 border-b px-5 py-4 sm:px-6">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/20 text-secondary">
            <Bell className="size-4" />
          </div>
          <div>
            <h3 className="font-display text-lg">ລາຍການລໍຖ້າສິນຄ້າ</h3>
            <p className="text-muted-foreground text-sm">
              {total > 0
                ? `${total} ລາຍການ${activeCount > 0 ? ` · ${activeCount} ກຳລັງດຳເນີນ` : ""}`
                : "ຍັງບໍ່ມີລາຍການລໍຖ້າ"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWriteWaitlist ? (
            <Button
              size="sm"
              className="rounded-full"
              asChild
            >
              <Link
                to="/app/restock-waitlist/new"
                search={{ customerId }}
              >
                <Plus data-icon="inline-start" />
                ບັນທຶກລາຍການ
              </Link>
            </Button>
          ) : null}
          {total > 0 ? (
            <Button
              size="sm"
              variant="outline"
              className="rounded-full border-secondary/30"
              asChild
            >
              <Link to="/app/restock-waitlist">ເບິ່ງທັງໝົດ</Link>
            </Button>
          ) : null}
        </div>
      </div>

      <div className="px-5 pb-5 sm:px-6">
        {waitlist.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-muted/20 px-4 py-8 text-center">
            <Package className="size-8 text-muted-foreground" />
            <p className="text-muted-foreground text-sm">
              {customerName} ຍັງບໍ່ມີລາຍການລໍຖ້າສິນຄ້າ
            </p>
            {canWriteWaitlist ? (
              <Button size="sm" className="rounded-full" asChild>
                <Link
                  to="/app/restock-waitlist/new"
                  search={{ customerId }}
                >
                  <Plus className="size-4" />
                  ບັນທຶກລາຍການແຮກ
                </Link>
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {rows.map((row) => (
              <WaitlistRow key={row.id} row={row} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
